/**
 * DataTable — DeCarbonIQ Design System
 * Standardized table wrapper with consistent header and row styling.
 *
 * Props:
 *   columns   — array of { label, align?, className? }
 *   children  — <tbody> rows (<tr><td>...)
 *   emptyState — JSX to show when no data
 *   loading   — shows spinner when true
 *   className — extra outer card classes
 *   testid    — data-testid
 */
import React from "react";
import { RefreshCw } from "lucide-react";

export function DataTable({
  columns = [],
  children,
  emptyState,
  loading = false,
  className = "",
  testid,
}) {
  const hasChildren = React.Children.count(children) > 0;

  return (
    <div
      className={`bg-white border border-slate-200 rounded-xl overflow-hidden ${className}`}
      data-testid={testid}
    >
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={16} className="animate-spin text-teal-500" />
        </div>
      ) : !hasChildren && emptyState ? (
        <div className="p-10 text-center">{emptyState}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            {columns.length > 0 && (
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {columns.map((col, i) => (
                    <th
                      key={i}
                      className={`px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider ${
                        col.align === "right"
                          ? "text-right"
                          : col.align === "center"
                          ? "text-center"
                          : "text-left"
                      } ${col.className || ""}`}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody className="divide-y divide-slate-100">{children}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DataTable;
